import type { Tower, TowerType, TowerBuff, ArcherFacingDirection, ArcherUnitState, MageFacingDirection, MageUnitState, SiegeFacingDirection, SiegeUnitState } from "@/types/tower.types"
import type { Enemy } from "@/types/enemy.types"
import type { Position } from "@/types/game.types"
import type { ObjectDecoration, LevelDecoration } from "@/types/tileset.types"
import { DECORATION_DEFS } from "@/types/tileset.types"
import { towerTypes } from "@/data/tower-types"
import { SOLDIER_NAMES } from "@/data/barracks-config"
import { generateId } from "@/utils/id-generator"
import { isPointOnPath } from "@/utils/collision"
import { angleToward } from "@/utils/pathfinding"
import { worldToTile, tileToWorldCenter, getTilesOccupied, canFitInGrid } from "@/utils/tile-utils"
import { TILE_SIZE } from "@/data/game-settings"
import { createBarracksState } from "@/systems/barracks-system"
import { gameNow } from "@/utils/game-clock"

/**
 * Create a new tower at the specified world position
 * Position will be snapped to tile grid
 */
export function createTower(type: TowerType, worldPosition: Position): Tower {
  const config = towerTypes[type]
  if (!config) {
    throw new Error(`Tower type "${type}" not found`)
  }

  const baseUpgrade = config.upgrades[0]
  const tile = worldToTile(worldPosition.x, worldPosition.y)

  // Calculate center position for multi-tile towers
  const centerX = tile.tileX * TILE_SIZE + (config.tileWidth * TILE_SIZE) / 2
  const centerY = tile.tileY * TILE_SIZE + (config.tileHeight * TILE_SIZE) / 2

  const position = { x: centerX, y: centerY }

  // Assign random name for archer, mage, siege towers (same name pool as soldiers)
  const randomName = (type === "archer" || type === "mage" || type === "siege")
    ? SOLDIER_NAMES[Math.floor(Math.random() * SOLDIER_NAMES.length)]
    : ""

  const tower: Tower = {
    id: generateId("tower"),
    type,
    name: randomName,
    position,
    tileX: tile.tileX,
    tileY: tile.tileY,
    tileWidth: config.tileWidth,
    tileHeight: config.tileHeight,
    level: 1,
    damage: baseUpgrade.damage,
    range: baseUpgrade.range,
    attackSpeed: baseUpgrade.attackSpeed,
    projectileSpeed: baseUpgrade.projectileSpeed,
    attackCooldown: 0,
    rotation: 0,
    target: null,
    color: config.baseColor,
    attackType: config.attackType,
    // Health
    health: baseUpgrade.maxHealth,
    maxHealth: baseUpgrade.maxHealth,
    // Animation state - start with building animation
    animationState: "building",
    animationStartTime: gameNow(),
  }

  // Initialize barracks state for barracks towers
  if (type === "barracks") {
    tower.barracksState = createBarracksState(position)
  }

  return tower
}

/**
 * Check if a tower can be placed at the given world position
 */
export function canPlaceTower(
  worldPosition: Position,
  towerType: TowerType,
  existingTowers: Tower[],
  path: Position[],
  pathWidth: number,
  objectDecorations?: ObjectDecoration[],
  decorations?: LevelDecoration[],
): boolean {
  const config = towerTypes[towerType]
  if (!config) return false

  const tile = worldToTile(worldPosition.x, worldPosition.y)

  // Check if tower fits in grid
  if (!canFitInGrid(tile.tileX, tile.tileY, config.tileWidth, config.tileHeight)) {
    return false
  }

  // Get all tiles this tower would occupy
  const occupiedTiles = getTilesOccupied(tile.tileX, tile.tileY, config.tileWidth, config.tileHeight)

  // Check each tile
  for (const t of occupiedTiles) {
    // Check if tile overlaps with path
    const tileCenter = tileToWorldCenter(t.tileX, t.tileY)
    if (isPointOnPath(tileCenter, path, pathWidth)) {
      return false
    }

    // Check if tile overlaps with existing towers
    for (const existingTower of existingTowers) {
      const existingTiles = getTilesOccupied(
        existingTower.tileX,
        existingTower.tileY,
        existingTower.tileWidth,
        existingTower.tileHeight,
      )
      for (const et of existingTiles) {
        if (et.tileX === t.tileX && et.tileY === t.tileY) {
          return false
        }
      }
    }

    // Check if tile overlaps with object decorations (unbuildable areas)
    if (objectDecorations) {
      for (const obj of objectDecorations) {
        for (let ox = 0; ox < obj.widthTiles; ox++) {
          for (let oy = 0; oy < obj.heightTiles; oy++) {
            if (t.tileX === obj.tileX + ox && t.tileY === obj.tileY + oy) {
              return false
            }
          }
        }
      }
    }

    // Check sheet-based decorations that are marked unbuildable (crystals,
    // rock formations, large plants placed by the tilemap generator)
    if (decorations) {
      for (const deco of decorations) {
        const def = DECORATION_DEFS[deco.type]
        if (!def || def.buildable) continue
        const w = def.coords.width ?? 1
        const h = def.coords.height ?? 1
        if (t.tileX >= deco.tileX && t.tileX < deco.tileX + w && t.tileY >= deco.tileY && t.tileY < deco.tileY + h) {
          return false
        }
      }
    }
  }

  return true
}

/**
 * Get cost of a tower at a given level
 * Applies heroism cost penalty (+2% per level) to all towers except shrines
 */
export function getTowerCost(type: TowerType, level = 1, heroismLevel = 0): number {
  const config = towerTypes[type]
  if (!config || level < 1 || level > config.upgrades.length) return 0

  const baseCost = config.upgrades[level - 1].cost

  // Shrines are exempt from heroism cost penalty
  if (type === "shrine") {
    return baseCost
  }

  // Apply +2% per heroism level penalty
  const penalty = 1 + 0.02 * heroismLevel
  return Math.floor(baseCost * penalty)
}

/**
 * Get max level for a tower type
 */
export function getMaxTowerLevel(type: TowerType): number {
  const config = towerTypes[type]
  return config ? config.upgrades.length : 3
}

/**
 * Upgrade a tower to the next level
 * Returns the upgraded tower, cost, and isBarracks flag for soldier upgrade handling
 */
export function upgradeTower(tower: Tower, heroismLevel = 0): { tower: Tower; cost: number; isBarracks: boolean } | null {
  const config = towerTypes[tower.type]
  if (!config) return null

  const maxLevel = config.upgrades.length
  if (tower.level >= maxLevel) return null

  const nextLevel = tower.level + 1
  const upgrade = config.upgrades[nextLevel - 1]

  // Apply heroism cost penalty (use getTowerCost for consistent penalty)
  const cost = getTowerCost(tower.type, nextLevel, heroismLevel)

  // Scale current health proportionally to new max health
  const healthPercent = tower.health / tower.maxHealth
  const newMaxHealth = upgrade.maxHealth
  const newHealth = Math.floor(newMaxHealth * healthPercent)

  return {
    tower: {
      ...tower,
      level: nextLevel,
      damage: upgrade.damage,
      range: upgrade.range,
      attackSpeed: upgrade.attackSpeed,
      projectileSpeed: upgrade.projectileSpeed,
      // Scale health proportionally
      health: newHealth,
      maxHealth: newMaxHealth,
      // Animation state - trigger upgrading animation
      animationState: "upgrading",
      animationStartTime: gameNow(),
      // Clear unit state during upgrade (unit disappears)
      archerState: undefined,
      mageState: undefined,
      // Preserve barracks state (rally point, soldier IDs)
      barracksState: tower.barracksState,
    },
    cost,
    isBarracks: tower.type === "barracks",
  }
}

/**
 * Find the closest enemy within tower's range
 * Uses simple distance calculation in world coordinates
 * @param rangeModifier - Optional multiplier for tower range (from upgrades)
 */
export function findTarget(tower: Tower, enemies: Enemy[], rangeModifier: number = 1): Enemy | null {
  let closestEnemy: Enemy | null = null
  const effectiveRange = tower.range * rangeModifier
  let closestDistSq = effectiveRange * effectiveRange

  for (const enemy of enemies) {
    // Skip dead enemies or enemies that reached the end
    if (enemy.health <= 0 || enemy.reachedEnd) continue

    // Calculate distance squared (faster than sqrt)
    const dx = enemy.position.x - tower.position.x
    const dy = enemy.position.y - tower.position.y
    const distSq = dx * dx + dy * dy

    if (distSq <= closestDistSq) {
      closestDistSq = distSq
      closestEnemy = enemy
    }
  }

  return closestEnemy
}

/**
 * Update tower state for one frame
 * @param deltaMs - Time since last frame in milliseconds
 * @param rangeModifier - Optional multiplier for tower range (from upgrades)
 */
export function updateTower(tower: Tower, enemies: Enemy[], deltaMs: number, rangeModifier: number = 1): Tower {
  const updated = { ...tower }

  // Update cooldown (convert frames to milliseconds: 60fps = 16.67ms per frame)
  // attackSpeed is in frames, so we convert deltaMs to frames
  const deltaFrames = deltaMs / 16.67
  if (updated.attackCooldown > 0) {
    updated.attackCooldown = Math.max(0, updated.attackCooldown - deltaFrames)
  }

  // Find a target (apply range modifier for archer range upgrades)
  const target = findTarget(updated, enemies, rangeModifier)

  if (target) {
    updated.target = target.id
    // Rotate toward target
    updated.rotation = angleToward(updated.position, target.position) + Math.PI / 2
  } else {
    updated.target = null
  }

  return updated
}

/**
 * Check if tower can fire this frame
 */
export function canTowerFire(tower: Tower): boolean {
  // Don't fire while building or upgrading
  if (tower.animationState === "building" || tower.animationState === "upgrading") {
    return false
  }
  return tower.attackCooldown <= 0 && tower.target !== null
}

/**
 * Fire the tower (reset cooldown)
 */
export function fireTower(tower: Tower): Tower {
  return {
    ...tower,
    attackCooldown: tower.attackSpeed,
  }
}

// === TOWER BUFF FUNCTIONS (for Embolden ultimate) ===

/**
 * Create a tower buff effect
 */
export function createTowerBuff(
  sourceHeroId: string,
  skillId: string,
  towerIds: string[],
  attackSpeedMultiplier: number,
  duration: number,
): TowerBuff {
  return {
    id: generateId("tower-buff"),
    sourceHeroId,
    skillId,
    affectedTowerIds: towerIds,
    attackSpeedMultiplier,
    remainingDuration: duration,
  }
}

/**
 * Get towers within a radius of a position
 */
export function getTowersInRadius(
  towers: Tower[],
  center: Position,
  radius: number,
): Tower[] {
  const radiusSq = radius * radius
  return towers.filter((tower) => {
    const dx = tower.position.x - center.x
    const dy = tower.position.y - center.y
    return dx * dx + dy * dy <= radiusSq
  })
}

/**
 * Get effective attack speed for a tower, applying any active buffs
 * Lower attack speed = faster attacks
 */
export function getEffectiveAttackSpeed(tower: Tower, activeBuffs: TowerBuff[]): number {
  let multiplier = 1.0

  for (const buff of activeBuffs) {
    if (buff.affectedTowerIds.includes(tower.id)) {
      // Multiply by the buff's attack speed multiplier (0.5 = 2x speed)
      multiplier *= buff.attackSpeedMultiplier
    }
  }

  return tower.attackSpeed * multiplier
}

/**
 * Fire the tower with buffs applied (reset cooldown with effective rate)
 */
export function fireTowerWithBuffs(tower: Tower, activeBuffs: TowerBuff[]): Tower {
  const effectiveAttackSpeed = getEffectiveAttackSpeed(tower, activeBuffs)
  return {
    ...tower,
    attackCooldown: effectiveAttackSpeed,
  }
}

/**
 * Update tower buffs - tick down duration and remove expired
 */
export function updateTowerBuffs(buffs: TowerBuff[], delta: number): TowerBuff[] {
  return buffs
    .map((buff) => ({ ...buff, remainingDuration: buff.remainingDuration - delta }))
    .filter((buff) => buff.remainingDuration > 0)
}

/**
 * Check if a tower is buffed
 */
export function isTowerBuffed(tower: Tower, activeBuffs: TowerBuff[]): boolean {
  return activeBuffs.some(
    (buff) => buff.affectedTowerIds.includes(tower.id) && buff.remainingDuration > 0,
  )
}

// === ARCHER UNIT STATE FUNCTIONS ===

/**
 * Get cardinal direction from one position to another
 */
export function getCardinalDirection(from: Position, to: Position): ArcherFacingDirection {
  const dx = to.x - from.x
  const dy = to.y - from.y

  // Determine if horizontal or vertical movement is dominant
  if (Math.abs(dx) > Math.abs(dy)) {
    return dx > 0 ? "right" : "left"
  } else {
    return dy > 0 ? "down" : "up"
  }
}

/**
 * Create initial archer unit state
 */
export function createArcherState(): ArcherUnitState {
  return {
    animationState: "idle",
    animationStartTime: gameNow(),
    facingDirection: "down",
  }
}

// Tower levels that don't show an archer unit (upgrade animation levels)
const ARCHER_HIDDEN_LEVELS = [4, 7]

/**
 * Update archer unit state for a tower
 * Handles animation state machine and facing direction
 * @param target - the tower's current target, already resolved by updateTower
 */
export function updateArcherState(
  tower: Tower,
  target: Enemy | null,
  _deltaMs: number,
): Tower {
  // Only apply to archer towers
  if (tower.type !== "archer") return tower

  // Don't show archer during building/upgrading
  if (tower.animationState !== "idle") {
    if (tower.archerState) {
      return { ...tower, archerState: undefined }
    }
    return tower
  }

  // Don't show archer on certain upgrade levels (4 and 7 are upgrade animation frames)
  if (ARCHER_HIDDEN_LEVELS.includes(tower.level)) {
    if (tower.archerState) {
      return { ...tower, archerState: undefined }
    }
    return tower
  }

  // Initialize archer state if missing (tower just finished building/upgrading)
  if (!tower.archerState) {
    return {
      ...tower,
      archerState: createArcherState(),
    }
  }

  const archerState = { ...tower.archerState }
  const now = gameNow()
  const elapsed = now - archerState.animationStartTime

  // Update facing direction based on target
  if (target) {
    archerState.facingDirection = getCardinalDirection(tower.position, target.position)
  }

  // Animation state machine
  switch (archerState.animationState) {
    case "preattack":
      // Preattack is brief (150ms), then transition to attack
      if (elapsed >= 150) {
        archerState.animationState = "attack"
        archerState.animationStartTime = now
      }
      break

    case "attack":
      // Attack animation lasts 400ms, then return to idle
      if (elapsed >= 400) {
        archerState.animationState = "idle"
        archerState.animationStartTime = now
      }
      break

    case "idle":
    default:
      // Idle loops continuously, no state change needed
      break
  }

  return { ...tower, archerState }
}

/**
 * Trigger archer preattack animation (call before firing)
 */
export function triggerArcherPreattack(tower: Tower): Tower {
  if (tower.type !== "archer" || !tower.archerState) return tower

  return {
    ...tower,
    archerState: {
      ...tower.archerState,
      animationState: "preattack",
      animationStartTime: gameNow(),
    },
  }
}

/**
 * Trigger archer attack animation (call when projectile fires)
 */
export function triggerArcherAttack(tower: Tower): Tower {
  if (tower.type !== "archer" || !tower.archerState) return tower

  return {
    ...tower,
    archerState: {
      ...tower.archerState,
      animationState: "attack",
      animationStartTime: gameNow(),
    },
  }
}

// === MAGE UNIT STATE FUNCTIONS ===

// Mage tower levels that don't show a mage unit (upgrade animation levels)
const MAGE_HIDDEN_LEVELS = [4, 7]

/**
 * Create initial mage unit state
 */
export function createMageState(): MageUnitState {
  return {
    animationState: "idle",
    animationStartTime: gameNow(),
    facingDirection: "down",
  }
}

/**
 * Update mage unit state for a tower
 * Handles animation state machine and facing direction
 * @param target - the tower's current target, already resolved by updateTower
 */
export function updateMageState(
  tower: Tower,
  target: Enemy | null,
  _deltaMs: number,
): Tower {
  // Only apply to mage towers
  if (tower.type !== "mage") return tower

  // Don't show mage during building/upgrading
  if (tower.animationState !== "idle") {
    if (tower.mageState) {
      return { ...tower, mageState: undefined }
    }
    return tower
  }

  // Don't show mage on certain upgrade levels (4 and 7 are upgrade animation frames)
  if (MAGE_HIDDEN_LEVELS.includes(tower.level)) {
    if (tower.mageState) {
      return { ...tower, mageState: undefined }
    }
    return tower
  }

  // Initialize mage state if missing (tower just finished building/upgrading)
  if (!tower.mageState) {
    return {
      ...tower,
      mageState: createMageState(),
    }
  }

  const mageState = { ...tower.mageState }
  const now = gameNow()
  const elapsed = now - mageState.animationStartTime

  // Update facing direction based on target
  if (target) {
    mageState.facingDirection = getCardinalDirection(tower.position, target.position) as MageFacingDirection
  }

  // Animation state machine (no preattack for mage)
  if (mageState.animationState === "attack" && elapsed >= 500) {
    mageState.animationState = "idle"
    mageState.animationStartTime = now
  }

  return { ...tower, mageState }
}

/**
 * Trigger mage attack animation (call when projectile fires)
 */
export function triggerMageAttack(tower: Tower): Tower {
  if (tower.type !== "mage" || !tower.mageState) return tower

  return {
    ...tower,
    mageState: {
      ...tower.mageState,
      animationState: "attack",
      animationStartTime: gameNow(),
    },
  }
}

// === SIEGE UNIT STATE FUNCTIONS ===

// Siege tower levels that don't show a catapult unit (upgrade animation levels)
const SIEGE_HIDDEN_LEVELS: number[] = []

/**
 * Get 8-direction facing from one position to another
 */
export function getSiegeDirection(from: Position, to: Position): SiegeFacingDirection {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const angle = Math.atan2(dy, dx) * (180 / Math.PI)

  // 8 directions, 45° each
  if (angle >= -22.5 && angle < 22.5) return "right"
  if (angle >= 22.5 && angle < 67.5) return "down-right"
  if (angle >= 67.5 && angle < 112.5) return "down"
  if (angle >= 112.5 && angle < 157.5) return "down-left"
  if (angle >= 157.5 || angle < -157.5) return "left"
  if (angle >= -157.5 && angle < -112.5) return "up-left"
  if (angle >= -112.5 && angle < -67.5) return "up"
  return "up-right"
}

/**
 * Create initial siege unit state
 */
export function createSiegeState(): SiegeUnitState {
  return {
    animationState: "idle",
    animationStartTime: gameNow(),
    facingDirection: "down",
  }
}

/**
 * Update siege unit state for a tower
 * Handles animation state machine and facing direction
 * @param target - the tower's current target, already resolved by updateTower
 */
export function updateSiegeState(
  tower: Tower,
  target: Enemy | null,
  _deltaMs: number,
): Tower {
  // Only apply to siege towers
  if (tower.type !== "siege") return tower

  // Don't show catapult during building/upgrading
  if (tower.animationState !== "idle") {
    if (tower.siegeState) {
      return { ...tower, siegeState: undefined }
    }
    return tower
  }

  // Don't show catapult on certain upgrade levels (4 and 7 are upgrade animation frames)
  if (SIEGE_HIDDEN_LEVELS.includes(tower.level)) {
    if (tower.siegeState) {
      return { ...tower, siegeState: undefined }
    }
    return tower
  }

  // Initialize siege state if missing (tower just finished building/upgrading)
  if (!tower.siegeState) {
    return {
      ...tower,
      siegeState: createSiegeState(),
    }
  }

  const siegeState = { ...tower.siegeState }
  const now = gameNow()
  const elapsed = now - siegeState.animationStartTime

  // Update facing direction based on target
  if (target) {
    siegeState.facingDirection = getSiegeDirection(tower.position, target.position)
  }

  // Animation state machine (attack lasts 600ms)
  if (siegeState.animationState === "attack" && elapsed >= 600) {
    siegeState.animationState = "idle"
    siegeState.animationStartTime = now
  }

  return { ...tower, siegeState }
}

/**
 * Trigger siege attack animation (call when projectile fires)
 */
export function triggerSiegeAttack(tower: Tower): Tower {
  if (tower.type !== "siege" || !tower.siegeState) return tower

  return {
    ...tower,
    siegeState: {
      ...tower.siegeState,
      animationState: "attack",
      animationStartTime: gameNow(),
    },
  }
}

// === TOWER DAMAGE FUNCTIONS ===

/**
 * Apply damage to a tower
 */
export function damageTower(tower: Tower, damage: number): Tower {
  const newHealth = Math.max(0, tower.health - damage)
  return { ...tower, health: newHealth }
}

/**
 * Check if a tower is destroyed (health <= 0)
 */
export function isTowerDestroyed(tower: Tower): boolean {
  return tower.health <= 0
}
